import React, { useState, useEffect, useRef } from 'react';

import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import CopyButton from '@/components/atoms/CopyButton/CopyButton';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Textarea } from '@/components/ui/textarea';
import useBrains from '@/hooks/useBrains';
import { selectBrainId } from '@/redux/session/selectors';

import { defaultStartingText } from './util';

const MAX_PROMPT = 1200;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const AutoBrainLandingPage = ({ control }: any) => {
  const { t } = useTranslation();

  const prefixRef = useRef<HTMLSpanElement | null>(null);
  const brainId = useSelector(selectBrainId);
  const { brain } = useBrains(brainId);
  const [indendation, setIndendation] = useState(0);
  const MAX_INPUT = MAX_PROMPT - defaultStartingText(brain?.language)?.length;
  const [expanded, setExpanded] = useState<string>('');

  const handleChange = (value: string) => {
    if (value.length > 500 && expanded) {
      setExpanded('');
    }
  };

  const example1 = t('auto_brain.prompt1', { lng: brain?.language });
  const example2 = t('auto_brain.prompt2', { lng: brain?.language });

  useEffect(() => {
    setIndendation(prefixRef.current?.clientWidth ?? 0);
  }, []);

  const exampleTextareaClass =
    'flex-1 resize-none rounded-md border border-border bg-background px-2 py-2 text-sm leading-5 text-foreground outline-none';

  return (
    <div>
      <Accordion
        type="single"
        collapsible
        value={expanded}
        onValueChange={setExpanded}
        className="flex flex-col gap-1"
      >
        <AccordionItem value="panel1" className="border-0">
          <AccordionTrigger className="w-fit justify-start gap-1 p-0 text-sm text-primary hover:no-underline">
            {expanded === 'panel1' ? t('common.hide') : t('common.view')}{' '}
            {t('auto_brain.simple_example')}
          </AccordionTrigger>
          <AccordionContent className="p-0 pt-2">
            <div className="flex items-center gap-4">
              <Textarea
                autoSize
                readOnly
                defaultValue={example1}
                className={exampleTextareaClass}
              />
              <CopyButton data={example1.slice(defaultStartingText(brain?.language)?.length)} />
            </div>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="panel2" className="border-0">
          <AccordionTrigger className="w-fit justify-start gap-1 p-0 text-sm text-primary hover:no-underline">
            {expanded === 'panel2' ? t('common.hide') : t('common.view')}{' '}
            {t('auto_brain.advanced_example')}
          </AccordionTrigger>
          <AccordionContent className="p-0 pt-2">
            <div className="flex items-center gap-4">
              <Textarea
                autoSize
                readOnly
                defaultValue={example2}
                className={exampleTextareaClass}
              />
              <CopyButton data={example2.slice(defaultStartingText(brain?.language)?.length)} />
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      <span className="mt-5 mb-3 inline-block text-sm text-muted-foreground">
        {t('auto_brain.tooltip')}
      </span>

      <form autoComplete="off" className="relative">
        <span
          ref={prefixRef}
          className="absolute z-1 translate-x-2 translate-y-[9px] text-sm leading-5 text-foreground"
        >
          {defaultStartingText(brain?.language)}
        </span>

        <Controller
          render={({ field: { value, onChange } }) => {
            return (
              <>
                <Textarea
                  onChange={(data) => {
                    handleChange(value);
                    onChange(data);
                  }}
                  value={value}
                  minRows={4}
                  maxLength={MAX_INPUT}
                  style={{ textIndent: `${indendation + 3}px` }}
                  className="w-full resize-none rounded-md border border-border p-2 pb-8 text-sm leading-5 text-foreground outline-none"
                />
                <span className="absolute right-1 bottom-8 text-xs text-muted-foreground">
                  {value.length + defaultStartingText(brain?.language)?.length}/{MAX_PROMPT}{' '}
                  {t('common.characters')}
                </span>
              </>
            );
          }}
          control={control}
          name="prompt"
        />
        <span className="text-xs text-muted-foreground">*{t('auto_brain.minimum_prompt')}</span>
      </form>
    </div>
  );
};

export default AutoBrainLandingPage;
